/**
 * Emote lifecycle decisions — the pure state logic behind the "stuck in the pose" fix.
 *
 * The captain GLB emote clips are long (wave ~5.4 s, dance ~7.8 s, sit ~11.4 s), so the old code — which
 * played every emote once, clamped on the last frame, and suppressed locomotion for the whole clip — glued the
 * avatar mid-pose (sliding) for many seconds with no way out. These helpers make the decision testable and keep
 * portscene.ts down to just the THREE crossfade calls:
 *   - a one-shot (wave) ends by itself at its clip end;
 *   - a loop (dance) runs until cancelled;
 *   - ANY real movement cancels either (so walking off never leaves the pose stuck);
 *   - re-pressing the SAME looping emote toggles it off.
 * No THREE, no DOM here → unit-tested directly.
 */
import { EMOTE_LOOP } from '@piratas/protocol';

/** An emote currently playing on an avatar: its clip name and the wall-clock ms it ends (`Infinity` = loops). */
export interface ActiveEmote { clip: string; until: number }

/** Measured ground speed (m/s) above which an emote is treated as cancelled even without the `moving` flag —
 *  a belt-and-suspenders backup to the authoritative walk signal, set well clear of interpolation jitter. */
export const EMOTE_MOVE_CANCEL_SPEED = 1.0;

/** Does emote `emoteId` loop until cancelled (dance) rather than play once (wave)? */
export function emoteLoops(emoteId: number): boolean { return EMOTE_LOOP[emoteId] ?? false; }

/** True when pressing `emoteId` (clip `clipName`) while `active` plays should TOGGLE the loop off — i.e. it is
 *  the same looping emote pressed again. One-shots never toggle; a different emote replaces instead. */
export function isEmoteToggleOff(emoteId: number, clipName: string, active: ActiveEmote | null): boolean {
  return !!active && active.clip === clipName && emoteLoops(emoteId);
}

/** The `until` timestamp for a freshly started emote: `Infinity` for a loop (dance), else `nowMs + durationMs`
 *  (a one-shot such as wave ends exactly at its clip end). */
export function emoteUntil(emoteId: number, nowMs: number, durationMs: number): number {
  return emoteLoops(emoteId) ? Infinity : nowMs + durationMs;
}

/** Should the active emote be cleared this frame? Any real movement cancels it (so the pose can never stick
 *  while the captain walks), and a one-shot additionally ends when its clip time is up. */
export function shouldEndEmote(active: ActiveEmote | null, moving: boolean, speed: number, nowMs: number): boolean {
  if (!active) return false;
  return moving || speed > EMOTE_MOVE_CANCEL_SPEED || nowMs >= active.until;
}
